import { z } from 'zod';
import { FileSummarySchema } from './files.js';
import { LocalDateSchema } from './scheduling.js';
/**
 * The clinic reports and the operations dashboard (M11.6, M11.1, API.md §2.5 and §2.11,
 * FR-REPORT-01..05, FR-ADMIN-02, rules §51).
 *
 * ## One envelope for four reports
 *
 * Every report answers the same three questions in the same order - *what is the range*,
 * *how did it move over time*, *what does it break down into* - so every report returns
 * the same four fields:
 *
 * | Field | Is | Why it is shared |
 * |---|---|---|
 * | `range` | the range and grouping **the server used** | rules §51.7: a metric carries its range, and the screen must render the range that was answered rather than the one the controls happen to show |
 * | `definitions` | one `MetricDefinition` per number on the report | rules §51.6: an ambiguous metric gets a written definition, and `timeBasis` is the half that is usually missing |
 * | `charts` | the time series, bucketed by `groupBy` | one shape, so a chart component is written once |
 * | `tables` | the breakdowns, as columns and rows | one shape, so **the CSV export is derived rather than written per report** |
 *
 * That last line is the reason `tables` is a generic `{ columns, rows }` rather than four
 * typed shapes. A report is several tables, not one (`ADMIN_REPORTS_UI.md` §2.7), and an
 * export that had to be hand-written per report is an export that drifts from the screen
 * it claims to be a copy of. `report-csv.ts` walks `tables` and knows nothing else.
 *
 * ## No PHI, by construction
 *
 * A cell is a string, a number or null, and every string a report produces is a
 * **category, a key, an opaque reference or a label the clinic itself configured**. No
 * report row carries a name, an email address, a phone number, a date of birth or a
 * member id, and the CSV export therefore cannot either - which is asserted against a
 * pattern set in the integration suite rather than promised here (rules §25.5, §51.4).
 *
 * The dashboard at the bottom of this file is the exception and is *not* a report: it is
 * a staff work queue behind `appointments.read`, and a patient's name on it is the point.
 * It is in this file because M11.1 and M11.6 are one backend surface, not because the two
 * obey the same rule.
 *
 * **No shape here takes a tenant id and none may.** The API resolves the clinic from the
 * host; a tenant id in a query string is ignored and logged (API.md §1.4, rules §18.4).
 */
/* ------------------------------------------------------------------ the reports ---- */
/**
 * The four reports, spelled as the URL spells them.
 *
 * `integration-health`, not `integrations`: the admin app routes it at
 * `/reports/integrations` because that is what a practice manager calls it, and
 * `REPORT_ENDPOINTS` in `apps/admin/src/lib/api/reports.ts` is the one place the two
 * names are mapped (`ADMIN_REPORTS_UI.md` §2.6).
 */
export const REPORT_IDS = [
    'funnel',
    'insurance-payments',
    'communications',
    'integration-health',
];
export const ReportIdSchema = z.enum(REPORT_IDS);
/**
 * Which permission opens which report, as SECURITY.md §5.1 spells it.
 *
 * `reports.export` is the fifth key and deliberately not one of these: it gates the CSV
 * route rather than a screen, and it is `no-impersonation`. There is no `reports.read`
 * key - the M11.6 brief named one, the permission catalog has never had one, and
 * inventing it would have widened the matrix in code without widening the document a
 * reviewer reads.
 */
export const REPORT_PERMISSIONS = Object.freeze({
    funnel: 'reports.funnel',
    'insurance-payments': 'reports.finance',
    communications: 'reports.communications',
    'integration-health': 'reports.integrations',
});
/**
 * The appointment-volume report, and why it is not a fifth `ReportId`.
 *
 * `REPORT_IDS` is the **reports hub**: the four screens the admin app enumerates, each
 * with a title, a path, an icon and a permission in a `Record<ReportId, …>`. Volume by
 * provider and by visit type is read from the scheduling screens rather than from the
 * hub, so adding it to that list would have put an entry in four exhaustive maps for a
 * screen that does not exist - and widened a published enum other apps switch on.
 *
 * What it *does* need is the export route, which is keyed by report. So there are two
 * lists: the hub's four, and everything the CSV route will render. Nothing that reads
 * `ReportId` has to change, and `POST /admin/reports/appointments/export` works.
 */
export const EXPORT_ONLY_REPORT_IDS = ['appointments'];
export const EXPORTABLE_REPORT_IDS = [...REPORT_IDS, ...EXPORT_ONLY_REPORT_IDS];
export const ExportableReportIdSchema = z.enum(EXPORTABLE_REPORT_IDS);
/**
 * Which permission opens the appointment-volume report.
 *
 * `reports.funnel` rather than a new key: SECURITY.md §5.1 has five report keys and this
 * milestone did not invent a sixth. Booking volume is the same question the funnel report
 * answers from the other end - how many visits did this clinic take - and the role that
 * may read one has no reason to be refused the other. API.md §2.11 already named this key
 * in the row that sketched the route.
 */
export const APPOINTMENTS_REPORT_PERMISSION = 'reports.funnel';
/** The longest range any report will answer. Beyond it the answer is a rollup, not a scan. */
export const REPORT_MAX_RANGE_DAYS = 366;
/* ------------------------------------------------------------------ the queries ---- */
/**
 * How the range is cut into buckets. Day and week only: a month grouping over a range
 * capped at 366 days yields at most twelve bars and loses the thing these reports are read
 * for - which Tuesday the drop-off started.
 *
 * Declared here rather than imported from `analytics.ts` so that `analytics.ts` can import
 * the shared report shapes from this file without the two forming a cycle. The two enums
 * are asserted equal in the contracts test.
 */
export const ReportGroupBySchema = z.enum(['day', 'week']);
/**
 * Every report's query: a range, a grouping, and nothing else that is not a filter.
 *
 * `from` and `to` are both **inclusive** `YYYY-MM-DD` in the clinic's own time zone. The
 * server turns that into `[from 00:00, to+1 day 00:00)` in that zone, so a Pacific
 * clinic's Monday morning booking lands in Monday rather than in Sunday (API.md §1.7).
 */
const RangeShape = {
    from: LocalDateSchema,
    to: LocalDateSchema,
    groupBy: ReportGroupBySchema.default('day'),
};
const endsAfterItBegins = {
    message: 'The range must end on or after it begins.',
    path: ['to'],
};
export const ReportRangeQuerySchema = z
    .object(RangeShape)
    .refine((value) => value.from <= value.to, endsAfterItBegins);
/** `GET /admin/reports/insurance-payments`. */
export const InsurancePaymentsQuerySchema = z
    .object({
    ...RangeShape,
    /** Narrow to one visit type, by key rather than by uuid (the report groups on it). */
    appointmentTypeKey: z.string().min(1).max(64).optional(),
    /** `visit`, `late_cancel` or `no_show`: the charge intent's kind. */
    intentKind: z.enum(['visit', 'late_cancel', 'no_show']).optional(),
})
    .refine((value) => value.from <= value.to, endsAfterItBegins);
/** `GET /admin/reports/communications`. */
export const CommunicationsQuerySchema = z
    .object({
    ...RangeShape,
    channel: z.enum(['email', 'sms']).optional(),
    kind: z.string().min(1).max(64).optional(),
})
    .refine((value) => value.from <= value.to, endsAfterItBegins);
/** `GET /admin/reports/integration-health`. */
export const IntegrationHealthQuerySchema = z
    .object({
    ...RangeShape,
    /** The catalog key (`tebra`, `stedi`, `acceptblue`, `mailgun`), never the install id. */
    integrationKey: z.string().min(1).max(64).optional(),
})
    .refine((value) => value.from <= value.to, endsAfterItBegins);
/* ------------------------------------------------------------------ the envelope ---- */
/**
 * A metric's written definition (rules §51.6).
 *
 * `timeBasis` is a separate field from `definition` because it is the part that is almost
 * always missing and almost always the reason two people disagree about a number:
 * "charges this week" counted by *creation* time and counted by *capture* time are
 * different reports of the same week. `PRODUCT.md` §9 names the basis for each metric and
 * so does this.
 */
export const MetricDefinitionSchema = z.object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    /** What it counts, in one plain sentence. No jargon, no formula notation. */
    definition: z.string().min(1).max(600),
    /** Which timestamp decides whether a record falls inside the range. */
    timeBasis: z.string().min(1).max(300),
    /** How to render it. `cents` is an integer number of cents, never a decimal. */
    unit: z.enum(['count', 'cents', 'percent', 'milliseconds', 'seconds', 'text']),
    /** Which reports show it. A metric may appear on more than one. */
    reports: z.array(ExportableReportIdSchema).min(1),
});
export const ReportDefinitionsResponseSchema = z.object({
    data: z.object({
        definitions: z.array(MetricDefinitionSchema),
        /** The clinic zone every report's days are bucketed in, so the UI can say which. */
        timeZone: z.string(),
        maxRangeDays: z.literal(REPORT_MAX_RANGE_DAYS),
    }),
});
/** The range the server actually used, echoed on every payload (rules §51.7). */
export const ReportRangeSchema = z.object({
    from: LocalDateSchema,
    to: LocalDateSchema,
    groupBy: ReportGroupBySchema,
    /** The clinic's IANA zone, so the screen can say which midnight the buckets cut on. */
    timeZone: z.string(),
});
/** A named series in a chart. The array order is the colour order and never changes. */
export const ReportSeriesRefSchema = z.object({
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(120),
});
/**
 * One bucket: `YYYY-MM-DD` (the day, or the Monday of the ISO week) and one value per
 * series key. Buckets are **dense** - a day with no rows is present with zeros, because
 * a line chart that skips empty days draws a slope where there was a gap.
 */
export const ReportBucketSchema = z.object({
    key: LocalDateSchema,
    label: z.string().min(1).max(40),
    values: z.record(z.string(), z.number()),
});
export const ReportChartSchema = z.object({
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(160),
    /**
     * What the numbers are. `ms` is the one that matters: percentile series must never be
     * summed or stacked, and the unit is how a generic renderer knows that.
     */
    unit: z.enum(['count', 'cents', 'ratio', 'ms']),
    series: z.array(ReportSeriesRefSchema),
    buckets: z.array(ReportBucketSchema),
});
/**
 * A column of a report table.
 *
 * `type` is the render hint *and* the CSV hint: a `cents` column is exported as an
 * integer number of cents with the unit in the header, never as a formatted currency
 * string a spreadsheet would re-parse.
 */
export const ReportColumnSchema = z.object({
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(120),
    type: z.enum(['text', 'count', 'cents', 'ratio', 'ms', 'seconds', 'date', 'instant']),
});
/** A cell. Strings are categories, keys, opaque references or clinic-configured labels. */
export const ReportCellSchema = z.union([z.string(), z.number(), z.null()]);
export const ReportTableSchema = z.object({
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(160),
    columns: z.array(ReportColumnSchema).min(1),
    /** Row arrays are positional and always the same length as `columns`. */
    rows: z.array(z.array(ReportCellSchema)),
});
/** What every report carries, whatever else it adds. */
export const ReportEnvelopeSchema = z.object({
    report: ReportIdSchema,
    range: ReportRangeSchema,
    definitions: z.array(MetricDefinitionSchema),
    charts: z.array(ReportChartSchema),
    tables: z.array(ReportTableSchema),
});
/* -------------------------------------------------- 2. insurance and payments ---- */
/**
 * `GET /admin/reports/insurance-payments` (FR-REPORT-02).
 *
 * Amounts are **cents, as integers**, everywhere. A report that rounded to dollars would
 * disagree with the payment panel on the same visit by a cent a row, and the first person
 * to notice would be an accountant.
 */
export const InsurancePaymentsTotalsSchema = z.object({
    /** Eligibility, counted by `eligibility_checks.checked_at`. */
    eligibilityChecked: z.number().int().min(0),
    eligibilityVerified: z.number().int().min(0),
    eligibilityInactive: z.number().int().min(0),
    eligibilityPending: z.number().int().min(0),
    eligibilityFailed: z.number().int().min(0),
    /** Quotes, counted by `price_quotes.created_at`. */
    quotesIssued: z.number().int().min(0),
    quotesSelfPay: z.number().int().min(0),
    quotesCopayEstimate: z.number().int().min(0),
    quotesPendingReview: z.number().int().min(0),
    /** Money, counted by `charges.created_at` and `refunds.created_at`. */
    chargedCount: z.number().int().min(0),
    chargedCents: z.number().int().min(0),
    declinedCount: z.number().int().min(0),
    declinedCents: z.number().int().min(0),
    refundedCount: z.number().int().min(0),
    refundedCents: z.number().int().min(0),
    /** Late-cancel and no-show intents raised in the range, and what they came to. */
    feeCount: z.number().int().min(0),
    feeCents: z.number().int().min(0),
    /**
     * Intents still waiting for money: `awaiting_amount`, `pending`, `scheduled` or
     * `processing`. **Not a subset of the range** - an intent raised before `from` and
     * still open is the one somebody has to act on, so this counts every open intent whose
     * appointment fell in the range.
     */
    outstandingIntents: z.number().int().min(0),
    outstandingCents: z.number().int().min(0),
});
export const InsurancePaymentsReportSchema = ReportEnvelopeSchema.extend({
    report: z.literal('insurance-payments'),
    totals: InsurancePaymentsTotalsSchema,
});
export const InsurancePaymentsResponseSchema = z.object({ data: InsurancePaymentsReportSchema });
/* --------------------------------------------------------- 3. communications ---- */
/**
 * `GET /admin/reports/communications` (FR-REPORT-03).
 *
 * **`complained` is counted apart from `failed` and never folded into it.** A spam
 * complaint is a permission problem, not a delivery problem, and it costs far more than a
 * bounce (`ADMIN_REPORTS_UI.md` §2.5).
 */
export const CommunicationsTotalsSchema = z.object({
    /** Counted by `notifications.created_at`; a message is one row per channel. */
    sent: z.number().int().min(0),
    delivered: z.number().int().min(0),
    failed: z.number().int().min(0),
    suppressed: z.number().int().min(0),
    complained: z.number().int().min(0),
    /** Contacts that went undeliverable or opted out inside the range. */
    optOuts: z.number().int().min(0),
    undeliverable: z.number().int().min(0),
    /** Reminder coverage: appointments starting in the range, and how many got both. */
    reminderAppointments: z.number().int().min(0),
    reminderBoth: z.number().int().min(0),
    reminderOneOnly: z.number().int().min(0),
    reminderNone: z.number().int().min(0),
    /**
     * Reminder timeliness (PRODUCT.md §9.3, TD-03): reminders whose `sent_at` was within
     * 60 seconds of `scheduled_for`, over reminders that were both scheduled and sent.
     * `null` when none were.
     */
    reminderOnTimeRate: z.number().min(0).max(1).nullable(),
    reminderMedianDelaySeconds: z.number().int().nullable(),
});
export const CommunicationsReportSchema = ReportEnvelopeSchema.extend({
    report: z.literal('communications'),
    totals: CommunicationsTotalsSchema,
});
export const CommunicationsResponseSchema = z.object({ data: CommunicationsReportSchema });
/* ----------------------------------------------------- 4. integration health ---- */
/**
 * `GET /admin/reports/integration-health` (FR-REPORT-04).
 *
 * Latency is `percentile_disc`, never `percentile_cont`: an interpolated latency is a
 * number no request ever took (the same choice `GET /admin/logs/requests/summary` makes).
 */
export const IntegrationHealthTotalsSchema = z.object({
    calls: z.number().int().min(0),
    errors: z.number().int().min(0),
    /** errors ÷ calls, 0 when there was no traffic. Four decimal places. */
    errorRate: z.number().min(0).max(1),
    p50Ms: z.number().int().min(0).nullable(),
    p95Ms: z.number().int().min(0).nullable(),
    /** Health checks that came back with a different result from the one before them. */
    healthTransitions: z.number().int().min(0),
    /** Dead-lettered jobs for this clinic, right now - a depth, not a range count. */
    deadLetters: z.number().int().min(0),
});
export const IntegrationHealthReportSchema = ReportEnvelopeSchema.extend({
    report: z.literal('integration-health'),
    totals: IntegrationHealthTotalsSchema,
});
export const IntegrationHealthResponseSchema = z.object({ data: IntegrationHealthReportSchema });
/* ------------------------------------------------------- 5. appointment volume ---- */
/** `GET /admin/reports/appointments`. */
export const AppointmentsReportQuerySchema = z
    .object({
    ...RangeShape,
    /** Narrow to one visit type, by key rather than by uuid (the report groups on it). */
    appointmentTypeKey: z.string().min(1).max(64).optional(),
    /** Narrow to one clinician. The uuid, because a provider has no stable key. */
    providerId: z.uuid().optional(),
})
    .refine((value) => value.from <= value.to, endsAfterItBegins);
/**
 * `GET /admin/reports/appointments` (FR-REPORT-01, API.md §2.11).
 *
 * Volume, counted by the **visit's own start time** rather than by when it was booked.
 * That is the opposite basis from the funnel report and both are deliberate: the funnel
 * asks "how well did last week's traffic convert", this one asks "how busy was last
 * week", and a visit booked in March for an appointment in June belongs to June here and
 * to March there. The metric definitions say so on every number.
 *
 * `completed`, `cancelled` and `noShow` are counted apart and never netted: a clinic
 * reading a no-show rate needs the numerator it is actually made of, and "booked minus
 * cancelled" hides which of the two moved.
 *
 * No PHI: every string is a visit-type key, a clinician's display name (already on the
 * schedule a patient is shown) or a status.
 */
export const AppointmentsTotalsSchema = z.object({
    /** Every non-deleted visit starting in the range, whatever became of it. */
    booked: z.number().int().min(0),
    completed: z.number().int().min(0),
    cancelled: z.number().int().min(0),
    noShow: z.number().int().min(0),
    /** Still `scheduled` or `confirmed` - the ones that have not resolved yet. */
    scheduled: z.number().int().min(0),
    /** Booked minutes across the range, so a clinic can read utilisation from volume. */
    bookedMinutes: z.number().int().min(0),
    /** Distinct clinicians and visit types that carried any volume. */
    providers: z.number().int().min(0),
    appointmentTypes: z.number().int().min(0),
    /** `noShow ÷ booked` and `cancelled ÷ booked`, four decimal places, 0 with no volume. */
    noShowRate: z.number().min(0).max(1),
    cancellationRate: z.number().min(0).max(1),
});
export const AppointmentsReportSchema = ReportEnvelopeSchema.extend({
    report: z.literal('appointments'),
    totals: AppointmentsTotalsSchema,
});
export const AppointmentsReportResponseSchema = z.object({ data: AppointmentsReportSchema });
/* --------------------------------------------------------------- CSV export ---- */
/**
 * `POST /admin/reports/:report/export` (FR-REPORT-05, rules §51.4, §51.5).
 *
 * A **POST**, not the `GET …?format=csv` API.md sketched, and the difference is the whole
 * point of the route: the export is a *side effect* - it writes a `files` row, it writes
 * an audit row, and it must not be replayed by a browser prefetch, a retry or a refresh.
 * `Idempotency-Key` makes the retry safe; a GET that wrote two files and two audit rows
 * would not be.
 *
 * The response is a signed download rather than a stream, so the bytes come from storage
 * exactly like every other file on the platform (`SECURITY.md` §12.3) and the audit row
 * names an object somebody can still find a week later.
 */
export const ReportExportRequestSchema = z
    .object({
    ...RangeShape,
    /** Report-specific filters, validated again by the report's own query schema. */
    filters: z.record(z.string().max(64), z.string().max(120)).optional(),
})
    .refine((value) => value.from <= value.to, endsAfterItBegins);
export const ReportExportResponseSchema = z.object({
    data: z.object({
        file: FileSummarySchema,
        /** Short-lived signed GET. Never logged, never stored (`SECURITY.md` §12.3). */
        url: z.string().max(4096),
        expiresAt: z.iso.datetime({ offset: true }),
        range: ReportRangeSchema,
        /** Data rows across every block, so the caller can say "1,204 rows exported". */
        rowCount: z.number().int().min(0),
    }),
});
/* ---------------------------------------------------------------- dashboard ---- */
/**
 * `GET /admin/dashboard` (M11.1, FR-ADMIN-02, API.md §2.5).
 *
 * **This is not a report and does not follow the rules above.** It is the work queue a
 * staff member opens in the morning, behind `appointments.read`, and a patient's name on
 * it is the point rather than a leak. Nothing here is exportable and nothing here is
 * aggregated for publication.
 *
 * Cached for 30 seconds per tenant in the API process (a TTL, not an invalidation: an
 * action taken on another screen is visible within half a minute, and that is the whole
 * promise being made).
 */
export const DashboardReadinessSchema = z.object({
    intake: z.enum(['ready', 'pending', 'missing']),
    insurance: z.enum(['ready', 'pending', 'missing']),
    card: z.enum(['ready', 'pending', 'missing']),
});
export const DashboardAppointmentSchema = z.object({
    id: z.uuid(),
    bookingReference: z.string(),
    startAt: z.iso.datetime({ offset: true }),
    endAt: z.iso.datetime({ offset: true }),
    clinicTimeZone: z.string(),
    status: z.string(),
    source: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    patientId: z.uuid(),
    /** Display name. The dashboard is a staff screen; `patients.read` is not needed to
     *  see a name on a visit you are already allowed to see. */
    patientName: z.string(),
    appointmentTypeId: z.uuid(),
    appointmentTypeName: z.string(),
    providerId: z.uuid().nullable(),
    providerName: z.string().nullable(),
    readiness: DashboardReadinessSchema,
});
export const DashboardReviewTaskSchema = z.object({
    id: z.uuid(),
    kind: z.string(),
    status: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    dueAt: z.iso.datetime({ offset: true }).nullable(),
    appointmentId: z.uuid().nullable(),
    /** Ids and a kind, never a patient's name: opening the task is the audited read
     *  (API.md §2.6). */
    assignedToUserId: z.string().nullable(),
});
export const DashboardFailedChargeSchema = z.object({
    chargeIntentId: z.uuid(),
    appointmentId: z.uuid(),
    bookingReference: z.string(),
    kind: z.string(),
    amountCents: z.number().int().min(0),
    currency: z.string().length(3),
    /** A decline class (`card_declined`, `gateway_error`), never the processor's sentence. */
    lastErrorClass: z.string().nullable(),
    failedAt: z.iso.datetime({ offset: true }),
});
export const DashboardIntegrationSchema = z.object({
    key: z.string(),
    name: z.string(),
    category: z.string(),
    /** `integration_installs.health`, plus `disabled` when the install is switched off. */
    health: z.enum(['unconfigured', 'configured', 'healthy', 'degraded', 'failed', 'disabled']),
    lastCheckedAt: z.iso.datetime({ offset: true }).nullable(),
    /** Short, human and safe to show: no credentials, no payload (`SECURITY.md` §11). */
    message: z.string().max(300).nullable(),
});
export const DashboardCountsSchema = z.object({
    todayTotal: z.number().int().min(0),
    /** Today's visits that have not started yet, by the clinic's clock. */
    todayRemaining: z.number().int().min(0),
    needsAttention: z.number().int().min(0),
    pendingReviews: z.number().int().min(0),
    /** Charge intents that failed in the last seven days. */
    failedCharges: z.number().int().min(0),
    /** Visits in the next 48 h with any readiness leg not ready (FR-INTAKE-07). */
    notReady: z.number().int().min(0),
    /**
     * Notifications that are late rather than merely pending: `scheduled` or `queued`
     * and already past `scheduled_for`. A reminder due next Tuesday is not a problem,
     * and counting it here would make the badge permanently non-zero for every clinic
     * that sends reminders. `failed` is deliberately **not** included - it is
     * `failedNotifications` below, because "nobody has sent it yet" and "the vendor
     * refused it" are different actions for the same staff member
     * (`analytics/dashboard/dashboard.service.ts`).
     */
    unsentNotifications: z.number().int().min(0),
    failedNotifications: z.number().int().min(0),
    /** Waitlist offers sent and not yet answered. */
    waitlistOffersOpen: z.number().int().min(0),
});
export const DashboardSchema = z.object({
    /** The clinic-local day the figures are for. */
    date: LocalDateSchema,
    clinicTimeZone: z.string(),
    counts: DashboardCountsSchema,
    today: z.array(DashboardAppointmentSchema),
    recentBookings: z.array(DashboardAppointmentSchema),
    reviewTasks: z.array(DashboardReviewTaskSchema),
    failedCharges: z.array(DashboardFailedChargeSchema),
    integrations: z.array(DashboardIntegrationSchema),
    /** When this payload was computed. Up to 30 s old (see the cache note above). */
    generatedAt: z.iso.datetime({ offset: true }),
});
export const DashboardQuerySchema = z.object({
    /** The clinic-local day to answer for. Defaults to today in the clinic's zone. */
    date: LocalDateSchema.optional(),
});
export const DashboardResponseSchema = z.object({ data: DashboardSchema });
//# sourceMappingURL=reports.js.map